import { EventBus } from "../core/EventBus.ts";
import { createRng, randomSeed } from "../core/Rng.ts";
import type { Rng } from "../core/Rng.ts";
import { Enemy } from "../entities/Enemy.ts";
import { Projectile } from "../entities/Projectile.ts";
import { ObjectPool } from "../utils/ObjectPool.ts";
import { WaveManager } from "../systems/WaveManager.ts";
import { CombatSystem } from "../systems/CombatSystem.ts";
import { EconomySystem, resolveEconomyConfig } from "../systems/EconomySystem.ts";
import { SynergySystem } from "../systems/SynergySystem.ts";
import { DraftSystem } from "../systems/DraftSystem.ts";
import { ScoreSystem, resolveScoreConfig } from "../systems/ScoreSystem.ts";
import { AudioEngine } from "../systems/AudioEngine.ts";
import {
  GAME,
  SHIELD_SEED_SALT,
  MODIFIER_SEED_SALT,
  BREACH_SEED_SALT,
  ENEMY_SEED_SALT,
} from "../core/config.ts";
import type { RuntimeState } from "../game/types.ts";
import type { Vec2 } from "../core/types.ts";
import type { GameData } from "../data/DataLoader.ts";

/** Core headless simulation systems (no DOM, no audio). */
export interface SimulationSystems {
  eventBus: EventBus;
  rng: Rng;
  /**
   * BL-148 / D-247 — the ISOLATED breach RNG stream. The dynamic breach
   * channel's only random draw (`BreachSystem.shouldBossBreach`) historically
   * rode the SHARED combat `rng` (S149 §3) — the lone spawn-adjacent draw that
   * wasn't isolated like SPAWN/SHIELD/MODIFIER, so it perturbed crit/draft
   * ordering and any future breach randomness would shift every downstream
   * draw. This salted sub-stream (`rng.seed ^ BREACH_SEED_SALT`) gives it its
   * own deterministic sequence. Consumed by `BreachSystem` (production via
   * `main.ts`, and the real-grid breach witness that gates the move).
   */
  breachRng: () => number;
  /**
   * BL-151 / D-250 — the ISOLATED enemy-behavior RNG stream (`rng.seed ^
   * ENEMY_SEED_SALT`), the 5th isolated spawn/enemy stream. Exposed on the
   * `ENEMY_BEHAVIORS` context (via WaveManager) for any FUTURE rolled behaviour
   * (BL-152's Jammer / Aura-Host). UNCONSUMED this session — the 3 current
   * specials are deterministic, so this stream never advances and every fixture
   * stays byte-identical (creating it draws NOTHING from the shared `rng`).
   */
  enemyRng: () => number;
  projectilePool: ObjectPool<Projectile>;
  enemyPool: ObjectPool<Enemy>;
  economy: EconomySystem;
  waveManager: WaveManager;
  combatSystem: CombatSystem;
  synergySystem: SynergySystem;
  draftSystem: DraftSystem;
  scoreSystem: ScoreSystem;
}

/** Production systems including audio. */
export interface SystemsResult extends SimulationSystems {
  audioEngine: AudioEngine;
}

/**
 * Create headless simulation systems (DOM-free, synchronous, deterministic).
 * No fetch, no document, no audio engine.
 * Determinism is guaranteed by the seeded RNG (no randomSeed() fallback).
 */
export function createSimulationSystems(
  gs: RuntimeState,
  pathPixels: Vec2[],
  data: GameData,
  rng: Rng,
): SimulationSystems {
  const eventBus = new EventBus();
  const projectilePool = new ObjectPool<Projectile>(
    () => new Projectile(),
    100,
  );
  const enemyPool = new ObjectPool<Enemy>(() => new Enemy(), 50);
  // S156 / PT-41 / D-253 — resolve the central economy tuning (surplus cap +
  // the new kill-gold ramp) from `waves.json` `economy`; absent → the byte-safe
  // code default (identity ramp = pre-S156 economy).
  const economy = new EconomySystem(
    GAME.INITIAL_CREDITS,
    eventBus,
    resolveEconomyConfig(data.waves.economy),
  );
  const shieldSeed = (rng.seed ^ SHIELD_SEED_SALT) | 0;
  const modifierSeed = (rng.seed ^ MODIFIER_SEED_SALT) | 0;
  // BL-148: the isolated breach sub-stream — an independent mulberry32 derived
  // from the run seed by XOR salt (the SPAWN/SHIELD/MODIFIER pattern). Creating
  // it draws NOTHING from the shared `rng`, so combat/draft/crit ordering is
  // untouched (the three golden masters stay byte-identical); only the breach
  // roll's own draws move off the shared stream.
  const breachSeed = (rng.seed ^ BREACH_SEED_SALT) | 0;
  const breachStream = createRng(breachSeed);
  // BL-151: the isolated enemy-behavior sub-stream — same isolated derivation as
  // the breach stream. Creating it draws NOTHING from the shared `rng` (combat/
  // draft/crit ordering untouched), and no current handler draws from it, so the
  // three golden masters + the breach witness stay byte-identical. The seam for
  // BL-152's rolled behaviours.
  const enemyStream = createRng((rng.seed ^ ENEMY_SEED_SALT) | 0);
  const enemyRng = () => enemyStream.next();
  const waveManager = new WaveManager(
    data.waves,
    data.enemies,
    pathPixels,
    eventBus,
    enemyPool,
    shieldSeed,
    modifierSeed,
    enemyRng,
    // BL-152 (S154): the live tower list — the Jammer's onPostUpdate hook reads it
    // to suppress towers in range. Same `gs.towers` ref CombatSystem holds.
    gs.towers,
    // AC-10 / OQ-3 (Spec B): the breach SEED, not the live `breachStream`
    // cursor. WaveManager derives its own per-wave routing cursor from it, so
    // routing draws cannot interleave with the eruption rolls and shift
    // `shouldBossBreach`'s results as a side effect of spawn traffic.
    breachSeed,
  );
  const combatSystem = new CombatSystem(
    gs.towers,
    waveManager.enemies,
    projectilePool,
    eventBus,
    () => rng.next(),
  );
  const synergySystem = new SynergySystem(gs.towers, data.synergies, eventBus);
  const draftSystem = new DraftSystem(data.cards, eventBus, rng);
  // D-322 / AC-5 (Task-007) — resolve the combo tuning block from
  // `waves.json` `score`; absent → the byte-safe code default.
  const scoreSystem = new ScoreSystem(eventBus, resolveScoreConfig(data.waves.score));

  combatSystem.setDraftSystem(draftSystem);
  combatSystem.setSynergySystem(synergySystem);
  economy.setDraftSystem(draftSystem);

  return {
    eventBus,
    rng,
    breachRng: () => breachStream.next(),
    enemyRng,
    projectilePool,
    enemyPool,
    economy,
    waveManager,
    combatSystem,
    synergySystem,
    draftSystem,
    scoreSystem,
  };
}

/** Create production systems (headless core + audio + DOM setup). */
export async function createSystems(
  gs: RuntimeState,
  pathPixels: Vec2[],
  data: GameData,
  providedRng?: Rng,
): Promise<SystemsResult> {
  const rng = providedRng ?? createRng(randomSeed());
  if (import.meta.env.DEV) {
    console.info("[run] seed=", rng.seed);
  }

  const sim = createSimulationSystems(gs, pathPixels, data, rng);

  const audioEngine = new AudioEngine(sim.eventBus);
  // BL-220 — if the manifest fetch/parse fails, STILL register the music track so
  // the MusicDirector (which plays "theme") has a path to (re-)decode. SFX always
  // fall back to procedural buffers, so an empty-music fallback would leave the
  // game permanently silent of music; this keeps the loop recoverable.
  const manifest = await fetch("./data/audio-manifest.json")
    .then((r) => r.json())
    .catch(() => ({
      music: [{ name: "theme", path: "audio/theme.m4a", type: "music" }],
      sfx: [],
    }));
  await audioEngine.loadManifest(manifest);

  // Autoplay gate: browsers start the AudioContext "suspended" until the first
  // user gesture. armGestureResume() mounts broad one-shot listeners
  // (pointerdown/touchstart fire BEFORE click and are what mobile/iOS — the
  // strictest gate — actually unlock audio on) that resume the context (and the
  // deferred boot ambience + menu theme) at the earliest possible input.
  // BL-216: it is RE-ARMABLE, so main.ts can re-mount it after an app background
  // trip leaves the context suspended, and the next tap re-resumes audio.
  audioEngine.armGestureResume();

  return {
    ...sim,
    audioEngine,
  };
}
